import React, { useRef, useState } from "react";
import { Modal, Input, Button, Typography, message, Alert } from "antd";
import { CloseSquareFilled, LockFilled } from "@ant-design/icons";
import Header from "./Header";
import { useAuth } from "./context/AuthContext";
import { changePassword } from "./service/service";
import { useNavigate } from "react-router-dom";
import type { InputRef } from "antd";
const { Text } = Typography;

const ChangePasswordModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  resetWithOutCurrentPassword?: boolean;
}> = ({ visible, onClose, resetWithOutCurrentPassword }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordValidation, setPasswordValidation] = useState({
    hasSpecialCharacters: false,
    hasAlphaNumeric: false,
    hasUpperCase: false,
    isSixLettersLong: false,
    isNewConfirmMatch: false,
  });
  const currentPasswordRef = useRef<InputRef>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleResetPassword = async () => {
    try {
      if (!user) {
        message.error(
          "Failed to change password as unable to determine the current logged-in user. Please log off and try again."
        );
      } else {
        await changePassword(user.id, currentPassword, newPassword);
        message.success(
          "Successfully changed password. Please use the new password from now on."
        );
      }
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordValidation({
        hasSpecialCharacters: false,
        hasAlphaNumeric: false,
        hasUpperCase: false,
        isSixLettersLong: false,
        isNewConfirmMatch: false,
      });
      onClose();
      logout();
      navigate("/");
    } catch (error: any) {
      console.error("Error changing password:", error);
      message.error(
        `Unable to change password: ${error?.response?.data?.exception}`
      );
      setConfirmPassword("");
      setNewPassword("");
      setCurrentPassword("");
    }
    if (currentPasswordRef.current) {
      currentPasswordRef.current.focus();
    }
  };

  const handlePasswordChange = (
    newPasswordValue: string,
    confirmPasswordValue: string
  ) => {
    // Validate password requirement
    setNewPassword(newPasswordValue);
    setConfirmPassword(confirmPasswordValue);
    const hasSpecialCharacters = /[!@#$%^&*(),.?":{}|<>]/.test(
      newPasswordValue
    );
    const hasAlphaNumeric = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]/.test(
      newPasswordValue
    );
    const hasUpperCase = /[A-Z]/.test(newPasswordValue);
    const isSixLettersLong = newPasswordValue.length >= 6;
    const isNewConfirmMatch = newPasswordValue === confirmPasswordValue;

    // Update password validation state
    setPasswordValidation({
      hasSpecialCharacters,
      hasAlphaNumeric,
      hasUpperCase,
      isSixLettersLong,
      isNewConfirmMatch,
    });
  };

  const isPasswordValid =
    passwordValidation.hasSpecialCharacters &&
    passwordValidation.hasAlphaNumeric &&
    passwordValidation.hasUpperCase &&
    passwordValidation.isSixLettersLong &&
    passwordValidation.isNewConfirmMatch;

  return (
    <Modal
      title={
        <Header
          header="Change Password"
          subheader="Use the form bleow to change your password."
        />
      }
      width={600}
      visible={visible}
      onCancel={onClose}
      footer={null}
      closeIcon={null}
      className="no-padding-modal"
    >
      <div style={{ padding: "10px" }}>
        <Alert
          banner
          message={`Once you change your password successfully, you will be navigated to login..`}
          closable={{
            "aria-label": "close",
            closeIcon: <CloseSquareFilled style={{ color: "white" }} />,
          }}
          type="warning"
        />
      </div>
      <div style={{ padding: "10px" }}>
        {!resetWithOutCurrentPassword && (
          <Input.Password
            placeholder="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            style={{ marginBottom: "10px" }}
            ref={currentPasswordRef}
          />
        )}
        <Input.Password
          placeholder="New Password"
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            handlePasswordChange(e.target.value, confirmPassword);
          }}
          style={{ marginBottom: "10px" }}
        />
        <Input.Password
          placeholder="Confirm New Password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            handlePasswordChange(newPassword, e.target.value);
          }}
        />

        {/* Display password requirements */}
        <div style={{ marginTop: "10px" }}>
          <Text type="secondary">Password Requirements:</Text>
          <ul>
            <li>
              {passwordValidation.hasSpecialCharacters ? (
                <Text type="success">Must have special characters</Text>
              ) : (
                <Text type="danger">Must have special characters</Text>
              )}
            </li>
            <li>
              {passwordValidation.hasAlphaNumeric ? (
                <Text type="success">Must have alphanumeric</Text>
              ) : (
                <Text type="danger">Must have alphanumeric</Text>
              )}
            </li>
            <li>
              {passwordValidation.hasUpperCase ? (
                <Text type="success">Must have uppercase letter</Text>
              ) : (
                <Text type="danger">Must have uppercase letter</Text>
              )}
            </li>
            <li>
              {passwordValidation.isSixLettersLong ? (
                <Text type="success">Must be 6 letters long</Text>
              ) : (
                <Text type="danger">Must be 6 letters long</Text>
              )}
            </li>
            <li>
              {passwordValidation.isNewConfirmMatch ? (
                <Text type="success">
                  New and confirm password should match
                </Text>
              ) : (
                <Text type="danger">New and confirm password should match</Text>
              )}
            </li>
          </ul>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "20px",
            gap: "5px",
          }}
        >
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button
            key="reset"
            type="primary"
            onClick={handleResetPassword}
            disabled={!isPasswordValid}
          >
            Reset
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ChangePasswordModal;
